import { Controller } from "@hotwired/stimulus"

// Pulls a fresh conversation list without a page reload — the queues do not poll, so sitting
// on one leaves it stale until the next search, sort, tab or write.
//
// The GET is the same list the toolbar already asks for. It is applied to the rows frame and
// the tab strip only: those are the two things that go stale, and replacing the conversations
// frame around them would remount this control (resetting the cooldown) and the selection
// controller the rows sit under. The thread pane is a different frame and is left alone, so
// an open thread stays open even when its row has left the list.
//
// The toolbar form is the source of truth for q/sort/filters/tab — search never writes those
// into the address bar, because it targets the inner frame on purpose. ?open is read off the
// pane rather than the URL for the same reason list_context_controller does: the two disagree.
const CONVERSATIONS_FRAME = "conversations"
const LIST_FRAME = "conversation_list"
const TABS_ID = "conversation_tabs"

export default class extends Controller {
  static targets = ["button"]
  static values = { cooldown: { type: Number, default: 12_000 } }

  connect() {
    this.inFlight = false
    this.lastSuccessfulAt = 0
    this.syncButton()
  }

  disconnect() {
    clearTimeout(this.unlockTimeout)
  }

  refresh() {
    if (this.unavailable) return

    this.inFlight = true
    this.syncButton()
    this.fetchList()
      .then((doc) => this.apply(doc))
      .then((applied) => {
        if (applied) this.lastSuccessfulAt = Date.now()
      })
      .catch(() => {})
      .finally(() => {
        this.inFlight = false
        this.syncButton()
        this.scheduleUnlock()
      })
  }

  async fetchList() {
    const response = await fetch(this.refreshUrl(), {
      headers: {
        Accept: "text/html",
        "Turbo-Frame": CONVERSATIONS_FRAME
      },
      credentials: "same-origin",
      cache: "no-store"
    })

    if (!response.ok) throw new Error("list refresh failed")

    return new DOMParser().parseFromString(await response.text(), "text/html")
  }

  // Streams rather than replaceWith, so Turbo reconnects the list's Stimulus controllers the
  // same way a search already does. A raw swap would leave the rows without list_controller.
  apply(doc) {
    const list = doc.getElementById(LIST_FRAME)
    if (!list) return false

    const streams = [ this.stream("update", LIST_FRAME, list.innerHTML) ]
    const tabs = doc.getElementById(TABS_ID)
    if (tabs) streams.push(this.stream("replace", TABS_ID, tabs.outerHTML))

    window.Turbo.renderStreamMessage(streams.join(""))
    return true
  }

  stream(action, target, html) {
    return `<turbo-stream action="${action}" target="${target}"><template>${html}</template></turbo-stream>`
  }

  refreshUrl() {
    const form = this.element.querySelector(".list__toolbar-form")
    const url = new URL(form?.action || window.location.href, window.location.origin)

    if (form) {
      new FormData(form).forEach((value, key) => {
        if (value) url.searchParams.set(key, value)
        else url.searchParams.delete(key)
      })
    }

    const openId = this.openThreadId
    if (openId) url.searchParams.set("open", openId)
    else url.searchParams.delete("open")

    return url.pathname + url.search
  }

  get openThreadId() {
    return document.querySelector(".thread[data-thread-id]")?.dataset.threadId
  }

  get unavailable() {
    return this.inFlight || this.coolingDown
  }

  get coolingDown() {
    return Date.now() - this.lastSuccessfulAt < this.cooldownValue
  }

  syncButton() {
    if (!this.hasButtonTarget) return

    this.buttonTarget.disabled = this.unavailable
    this.buttonTarget.setAttribute("aria-busy", this.inFlight ? "true" : "false")
    this.buttonTarget.classList.toggle("is-busy", this.inFlight)
    this.buttonTarget.classList.toggle("is-cooling", !this.inFlight && this.coolingDown)
  }

  scheduleUnlock() {
    clearTimeout(this.unlockTimeout)
    if (!this.coolingDown) return

    this.unlockTimeout = setTimeout(() => this.syncButton(),
                                    this.cooldownValue - (Date.now() - this.lastSuccessfulAt))
  }
}
